/**
 * Frontend Permission Helper
 * Determines what UI elements to show based on user role and ownership
 */

// Management roles that can create/edit/delete projects and tasks
const MANAGEMENT_ROLES = ['Head of Department', 'Manager', 'Developer'];

/**
 * Check if user has a management role
 */
export const isManagementRole = (user) => {
    if (!user || !user.role) return false;
    return MANAGEMENT_ROLES.includes(user.role);
};

/**
 * Check if user can manage projects (create/edit/delete)
 * Only management roles can manage projects
 */
export const canManageProjects = (user) => {
    return isManagementRole(user);
};

/**
 * Check if user can manage tasks (create/edit/delete)
 * Only management roles can manage tasks
 */
export const canManageTasks = (user) => {
    return isManagementRole(user);
};

/**
 * Check if user is a project owner
 */
export const isProjectOwner = (user, project) => {
    if (!user || !project) return false;

    // Check new multi-owner structure
    if (project.owners && Array.isArray(project.owners)) {
        return project.owners.some(owner => owner.user_id === user.id || owner.id === user.id);
    }

    // Fallback to old single assignee_id for backward compatibility
    if (project.assignee_id === user.id) return true;

    return false;
};

/**
 * Check if user is a task assignee
 */
export const isTaskAssignee = (user, task) => {
    if (!user || !task) return false;

    // Check new multi-assignee structure
    if (task.assignees && Array.isArray(task.assignees)) {
        return task.assignees.some(assignee => assignee.user_id === user.id || assignee.id === user.id);
    }

    // Fallback to old single assignee_id for backward compatibility
    if (task.assignee_id === user.id) return true;

    return false;
};

/**
 * Check if user is a subtask assignee
 */
export const isSubtaskAssignee = (user, subtask) => {
    if (!user || !subtask) return false;

    if (subtask.assignees && Array.isArray(subtask.assignees)) {
        return subtask.assignees.some(assignee => assignee.user_id === user.id || assignee.id === user.id);
    }

    return false;
};

/**
 * Check if user can update project status/progress
 * Management roles OR project owners can update
 */
export const canUpdateProjectStatus = (user, project) => {
    if (isManagementRole(user)) return true;
    return isProjectOwner(user, project);
};

/**
 * Check if user can update task status/progress
 * Management roles OR task assignees can update
 */
export const canUpdateTaskStatus = (user, task) => {
    if (isManagementRole(user)) return true;
    return isTaskAssignee(user, task);
};

/**
 * Check if user can update subtask status/progress
 * Management roles OR subtask assignees can update
 */
export const canUpdateSubtaskStatus = (user, subtask) => {
    if (isManagementRole(user)) return true;
    return isSubtaskAssignee(user, subtask);
};

/**
 * Check if user can add comments to a project
 * Management roles OR project owners can comment
 */
export const canCommentOnProject = (user, project) => {
    if (isManagementRole(user)) return true;
    return isProjectOwner(user, project);
};

/**
 * Check if user can add comments to a task
 * Management roles OR task assignees can comment
 */
export const canCommentOnTask = (user, task) => {
    if (isManagementRole(user)) return true;
    return isTaskAssignee(user, task);
};

/**
 * Check if user can add comments to a subtask
 * Management roles OR subtask assignees can comment
 */
export const canCommentOnSubtask = (user, subtask) => {
    if (isManagementRole(user)) return true;
    return isSubtaskAssignee(user, subtask);
};

/**
 * Check if user can create subtasks for a task
 * Management roles OR task assignees OR project owners can create subtasks
 */
export const canCreateSubtask = (user, task, project) => {
    if (isManagementRole(user)) return true;
    if (isTaskAssignee(user, task)) return true;
    if (project && isProjectOwner(user, project)) return true;
    return false;
};

/**
 * Check if user can edit a project
 * Only management roles can edit projects
 */
export const canEditProject = (user, project) => {
    return isManagementRole(user);
};

/**
 * Check if user can delete a project
 * Only management roles can delete projects
 */
export const canDeleteProject = (user, project) => {
    return isManagementRole(user);
};

/**
 * Check if user can edit a task
 * Only management roles can edit tasks
 */
export const canEditTask = (user, task) => {
    return isManagementRole(user);
};

/**
 * Check if user can delete a task
 * Only management roles can delete tasks
 */
export const canDeleteTask = (user, task) => {
    return isManagementRole(user);
};

/**
 * Get user's permission summary for display
 */
export const getUserPermissions = (user) => {
    return {
        canManageProjects: canManageProjects(user),
        canManageTasks: canManageTasks(user),
        isManagement: isManagementRole(user),
        role: user?.role || 'Unknown'
    };
};
